import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsSummaryCard from "../clients/Tools/kit/ToolsSummaryCard";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookInputFieldService from "../../services/PlaybookInputFieldService";
import UtilityService from "../../services/UtilityService";
import AgentSourceList from "./AgentSourceList";
import AgentRunRequestField from "./AgentRunRequestField";
import PlaybookInputFieldsForm from "./PlaybookInputFieldsForm";

import "./start-playbook-run-modal.css";

class StartPlaybookRunModal extends WComponent {
	constructor(props) {
		super(props);
		const periods = PlaybookRunService.recentCompletedMonths();
		const fields = PlaybookInputFieldService.fieldsOf({ version: props.agent });
		this.state = {
			periods,
			periodEnd: periods[0].value,
			request: "",
			fieldValues: PlaybookInputFieldService.initialValues({ fields }),
			isStarting: false,
			startError: null,
			sourceError: null,
			sources: [],
			canRun: false,
			isCheckingSources: true,
		};
	}

	componentDidMount() {
		const { agent, clients } = this.props;
		if (!agent) return;
		if (clients) {
			this.update({ canRun: clients.length > 0, isCheckingSources: false });
			return;
		}
		this.checkSources();
	}

	checkSources = async () => {
		const { agent, clientId } = this.props;
		await this.update({ isCheckingSources: true, sourceError: null });
		try {
			const { sources, canRun } = await PlaybookRunService.checkRunSources({ playbookId: agent.id, clientId });
			await this.update({ sources, canRun, isCheckingSources: false });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The playbook's sources could not be checked." });
			await this.update({ sources: [], canRun: false, isCheckingSources: false, sourceError: message });
		}
	};

	onPeriodChanged = periodEnd => {
		this.update({ periodEnd, startError: null });
	};

	onRequestChanged = request => {
		this.update({ request, startError: null });
	};

	onFieldChanged = ({ key, value }) => {
		this.setState(previousState => ({ fieldValues: { ...previousState.fieldValues, [key]: value }, startError: null }));
	};

	describeFieldState = () => {
		const { agent, clients } = this.props;
		const { fieldValues } = this.state;
		const fields = PlaybookInputFieldService.fieldsOf({ version: agent });
		const hasFields = fields.length > 0;
		const missingFields = PlaybookInputFieldService.missingRequired({ fields, values: fieldValues });
		const isOverOneClient = hasFields && !!clients && clients.length > 1;
		const fieldState = { fields, hasFields, missingFields, isOverOneClient };
		return fieldState;
	};

	onStartRun = async () => {
		const { agent, clientId, clients, onStarted } = this.props;
		const { periodEnd, request, fieldValues } = this.state;
		const { takesRequest, needsPeriod } = PlaybookRunService.runInputsNeededBy({ agent });
		const { fields, hasFields } = this.describeFieldState();
		const clientIds = clients ? clients.map(client => client.id) : [clientId];
		await this.update({ isStarting: true, startError: null });
		try {
			const runFieldValues = hasFields ? PlaybookInputFieldService.valuesForRun({ fields, values: fieldValues }) : null;
			const inputs = PlaybookRunService.inputsForRun({ needsPeriod, periodEnd, takesRequest, request, fieldValues: runFieldValues });
			const result = await PlaybookRunService.startRuns({ playbookId: agent.id, clientIds, inputs });
			const wasSkipped = result.willRun === 0 && result.skipped.length > 0;
			if (wasSkipped) {
				await this.update({ isStarting: false, startError: result.skipped[0].reason });
				return;
			}
			await this.update({ isStarting: false });
			onStarted({ runId: result.runId });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The run could not be started." });
			await this.update({ isStarting: false, startError: message });
		}
	};

	renderFooter() {
		const { onClose, agent, clients } = this.props;
		const { isStarting, canRun, isCheckingSources, request } = this.state;
		const startLabel = clients ? `Start ${clients.length} run${clients.length === 1 ? "" : "s"}` : "Start run";
		const { takesRequest } = PlaybookRunService.runInputsNeededBy({ agent });
		const isRequestReady = PlaybookRunService.isRunRequestReady({ takesRequest, request });
		const { missingFields, isOverOneClient } = this.describeFieldState();
		const areFieldsReady = missingFields.length === 0;
		const sourcesReason = canRun ? "" : "Link the required sources first";
		const requestReason = isRequestReady ? "" : "Say what you want first";
		const clientsReason = isOverOneClient ? "A run with a form is for one client at a time" : "";
		const fieldsReason = areFieldsReady ? "" : "Fill in the required fields first";
		const blockedReason = sourcesReason || requestReason || clientsReason || fieldsReason;

		return (
			<>
				<span className="start-playbook-run__footer-note">
					<Icon.ShieldOff size={14} />
					Reads only. Nothing is posted.
				</span>
				<div className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>
						Cancel
					</button>
					<button
						type="button"
						className="tk-button tk-button--primary"
						onClick={this.onStartRun}
						disabled={isStarting || isCheckingSources || !canRun || !isRequestReady || isOverOneClient || !areFieldsReady}
						title={blockedReason}
					>
						<Icon.Play size={14} />
						{isStarting ? "Starting…" : startLabel}
					</button>
				</div>
			</>
		);
	}

	renderPeriod() {
		const { periods, periodEnd } = this.state;
		return (
			<div className="tk-form-field">
				<div className="tk-form-label">
					Period<span className="tk-form-required">*</span>
				</div>
				<ToolsSelect value={periodEnd} options={periods} onChange={this.onPeriodChanged} />
				<div className="tk-form-help">The month to review. Balances are read as at its last day.</div>
			</div>
		);
	}

	renderSources() {
		const { onConnect } = this.props;
		const { sourceError, sources, canRun, isCheckingSources } = this.state;
		const missingRequired = sources.filter(source => source.isRequired && !source.isConnected);
		const hasNothingToShow = !isCheckingSources && sources.length === 0 && !sourceError;
		if (hasNothingToShow) return null;

		return (
			<div className="tk-form-field">
				<div className="tk-form-label">Sources</div>
				{isCheckingSources ? <div className="tk-form-help">Checking what is linked…</div> : <AgentSourceList sources={sources} onConnect={onConnect} />}
				{sourceError && (
					<div className="tk-notice tk-notice--warning start-playbook-run__error">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{sourceError}
						</div>
					</div>
				)}
				{!isCheckingSources && !canRun && missingRequired.length > 0 && (
					<div className="tk-form-help start-playbook-run__blocked">
						Connect {missingRequired.map(source => source.label).join(" and ")} before running this agent.
					</div>
				)}
				{!isCheckingSources && canRun && sources.some(source => !source.isConnected) && (
					<div className="tk-form-help">It will run without the helpful sources that are not linked and say what it could not read.</div>
				)}
			</div>
		);
	}

	render() {
		const { open, onClose, agent, clientName, clients } = this.props;
		const { request, startError, fieldValues, isStarting } = this.state;
		if (!agent) return null;
		const version = agent.current_version;
		const { takesRequest, needsPeriod } = PlaybookRunService.runInputsNeededBy({ agent });
		const { fields, hasFields, isOverOneClient } = this.describeFieldState();
		const isWide = takesRequest || hasFields;
		const chooseOneNote = isOverOneClient ? " Cancel, then choose One client to run it." : "";
		const clientsText = clients ? `${clients.length} connected client${clients.length === 1 ? "" : "s"}` : clientName;
		const meta = clientsText ? [{ icon: <Icon.Briefcase size={13} />, text: clientsText }] : [];

		const context = <ToolsSummaryCard icon={<Icon.Cpu size={16} />} tone="accent" title={version.name} meta={meta} />;

		return (
			<ToolsModal open={open} onClose={onClose} title="Run playbook" width={isWide ? 600 : 520} context={context} footer={this.renderFooter()}>
				{takesRequest && <AgentRunRequestField value={request} example={version.request_example} onChange={this.onRequestChanged} isAutoFocused />}
				{needsPeriod && this.renderPeriod()}
				{hasFields && !isOverOneClient && <PlaybookInputFieldsForm fields={fields} values={fieldValues} onChange={this.onFieldChanged} disabled={isStarting} />}
				{clients && (
					<div className="tk-form-field">
						<div className="tk-form-label">Clients</div>
						{hasFields && (
							<div className="tk-form-help start-playbook-run__blocked">
								A run with a form is for one client at a time, since its values belong to that client.{chooseOneNote}
							</div>
						)}
						{!hasFields && (
							<div className="tk-form-help">
								Runs on the {clients.length} client{clients.length === 1 ? "" : "s"} with every required source linked, a few at a time. Each
								gets its own run and its own findings, grouped under one result you can open now and watch fill in.
							</div>
						)}
					</div>
				)}
				{!clients && this.renderSources()}
				{startError && (
					<div className="tk-notice tk-notice--warning start-playbook-run__error">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{startError}
						</div>
					</div>
				)}
			</ToolsModal>
		);
	}
}

export default StartPlaybookRunModal;
